import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  Shield, 
  Users, 
  FileText, 
  Settings,
  TrendingUp,
  UserCheck,
  LogOut,
  Crown,
  BarChart3,
  Database,
  Activity,
  User,
  ChevronDown,
  Mic,
  Lock,
  CheckCircle,
  XCircle,
  Mail,
  Sparkles,
  ClipboardList
} from 'lucide-react';

// Using relative URLs
const API = '/api';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to fetch admin statistics');
      console.error('Admin stats fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Super Admin</h1>
                <p className="text-sm text-slate-600">Nova Platform Management</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.full_name || user?.email}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8 animate-fadeIn">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            Platform Overview
          </h2>
          <p className="text-slate-600">
            Monitor and manage the Nova SaaS platform, customers, and AI configurations.
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-slideIn">
            <Card className="hover-lift border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Customers</p>
                    <p className="text-3xl font-bold text-slate-900">{stats.total_customers}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Audits</p>
                    <p className="text-3xl font-bold text-navy-600">{stats.total_audits}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-navy-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Controls</p>
                    <p className="text-3xl font-bold text-purple-600">{stats.total_controls}</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                    <Database className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover-lift border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Recent Signups</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.recent_signups}</p>
                    <p className="text-xs text-slate-500">Last 30 days</p>
                  </div>
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Management Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 animate-slideIn">
          {/* Customer Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/customers')}
            data-testid="customers-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Customers</CardTitle>
                  <CardDescription>Manage all customer accounts and emulation</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">View all customers</span>
                  <UserCheck className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Emulate customer accounts</span>
                  <Activity className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Monitor user activity</span>
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signup Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/signups')}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Signups</CardTitle>
                  <CardDescription>Review and approve new user signups</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Pending approvals</span>
                  <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-200">
                    {stats?.pending_signups || 0}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Approve or reject signups</span>
                  <CheckCircle className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Review rejected accounts</span>
                  <XCircle className="w-4 h-4 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* All Audits Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/super-admin/all-audits')}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-navy-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Audits</CardTitle>
                  <CardDescription>View and manage all customer audits</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">View all audits</span>
                  <FileText className="w-4 h-4 text-navy-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Assign auditors to audits</span>
                  <UserCheck className="w-4 h-4 text-navy-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Track audit progress</span>
                  <BarChart3 className="w-4 h-4 text-navy-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Super Admin Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/super-admin-management')}
            data-testid="super-admin-management-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Super Admin Management</CardTitle>
                  <CardDescription>Manage super admin users and permissions</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Add new super admins</span>
                  <UserCheck className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Reset passwords</span>
                  <Lock className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Remove admin access</span>
                  <Shield className="w-4 h-4 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/email-management')}
            data-testid="email-management-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Email Management</CardTitle>
                  <CardDescription>Configure Resend API and email templates</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Email templates</span>
                  <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-200">
                    Ready
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Resend integration</span>
                  <Settings className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Send test emails</span>
                  <Mail className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* File Storage */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/r2-settings')}
            data-testid="r2-settings-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                  <Database className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">File Storage</CardTitle>
                  <CardDescription>Configure AWS S3 or Cloudflare R2</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">S3/R2 credentials</span>
                  <Settings className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">File size limits</span>
                  <Database className="w-4 h-4 text-orange-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Allowed file types</span>
                  <Shield className="w-4 h-4 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Environment Settings */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/environment-settings')}
            data-testid="environment-settings-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-200 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Environment Settings</CardTitle>
                  <CardDescription>Manage Dev/Prod Variables</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">API Keys</span>
                  <Lock className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Database URLs</span>
                  <Database className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Auto-detect Environment</span>
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* OpenAI Settings */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/openai-settings')}
            data-testid="openai-settings-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-violet-200 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">OpenAI Settings</CardTitle>
                  <CardDescription>API key + per-feature model selection</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">API key</span>
                  <Lock className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Test connection</span>
                  <CheckCircle className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Model per purpose</span>
                  <Settings className="w-4 h-4 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Server Tools (LibreOffice install, etc.) */}
          <Card
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/server-tools')}
            data-testid="server-tools-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-200 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-amber-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">Server Tools</CardTitle>
                  <CardDescription>Install LibreOffice for office-document previews</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">.docx / .xlsx / .pptx preview</span>
                  <CheckCircle className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Runs idempotent install script</span>
                  <Activity className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Audit-logged</span>
                  <Shield className="w-4 h-4 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MCP Connections (ChatGPT) */}
          <Card
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/mcp')}
            data-testid="mcp-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <CardTitle className="text-lg">MCP Connections</CardTitle>
                  <CardDescription>Manage ChatGPT / MCP connectors, scopes & logs</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Workspace & global scopes</span>
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Read / write / global capabilities</span>
                  <Activity className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Request logs & rotate/revoke</span>
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Prompt Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/prompts')}
            data-testid="prompts-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-navy-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Audit Prompts</CardTitle>
                  <CardDescription>Configure AI prompts for control generation</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">SOC 2 prompts</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">GDPR prompts</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">ISO 27001 & PCI DSS</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice Prompts Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/super-admin/voice-prompts')}
            data-testid="voice-prompts-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                  <Mic className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Voice Prompts</CardTitle>
                  <CardDescription>Configure AI prompts for voice assessments</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">SOC 2 Type 1 & 2</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">GDPR & HIPAA</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">ISO 27001 & PCI DSS</span>
                  <Badge variant="outline" className="text-xs">Active</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auditors Management */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/auditors')}
            data-testid="auditors-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Auditors</CardTitle>
                  <CardDescription>Manage platform-level auditors</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Create auditors</span>
                  <UserCheck className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Assign to customers</span>
                  <Users className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Manage assignments</span>
                  <Settings className="w-4 h-4 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Technologies Master Data */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/super-admin/technologies')}
            data-testid="technologies-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Technologies</CardTitle>
                  <CardDescription>Manage technology master data</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Manage categories</span>
                  <Shield className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Add technologies</span>
                  <Settings className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Configure master list</span>
                  <FileText className="w-4 h-4 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice Agent Settings */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/super-admin/audit-profile')}
            data-testid="audit-profile-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Audit Profile</CardTitle>
                  <CardDescription>Manage master audit questions</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Global questions</span>
                  <ClipboardList className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Archive / unarchive</span>
                  <Shield className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Propagates to all customers</span>
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice Agent Settings */}
          <Card 
            className="hover-lift cursor-pointer border-0 shadow-soft bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-xl"
            onClick={() => navigate('/admin/voice-agent')}
            data-testid="voice-agent-card"
          >
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-teal-200 rounded-xl flex items-center justify-center">
                  <Mic className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Voice Agent</CardTitle>
                  <CardDescription>Configure AI voice assessment settings</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">OpenAI Realtime API</span>
                  <Settings className="w-4 h-4 text-teal-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">API key management</span>
                  <Shield className="w-4 h-4 text-teal-600" />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Model & voice settings</span>
                  <Mic className="w-4 h-4 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Platform Health */}
          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Health</CardTitle>
                  <CardDescription>System status and performance</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">API Status</span>
                  <Badge className="bg-green-100 text-green-700 text-xs">Healthy</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">AI Integration</span>
                  <Badge className="bg-green-100 text-green-700 text-xs">Active</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Database</span>
                  <Badge className="bg-green-100 text-green-700 text-xs">Connected</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 animate-slideIn">
          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common administrative tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Button 
                  variant="outline" 
                  className="flex items-center space-x-2 h-12"
                  onClick={() => navigate('/admin/customers')}
                >
                  <Users className="w-4 h-4" />
                  <span>View Customers</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center space-x-2 h-12"
                  onClick={() => navigate('/admin/prompts')}
                >
                  <Settings className="w-4 h-4" />
                  <span>Edit Audit Prompts</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center space-x-2 h-12"
                  onClick={() => navigate('/super-admin/voice-prompts')}
                >
                  <Mic className="w-4 h-4" />
                  <span>Edit Voice Prompts</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center space-x-2 h-12"
                  onClick={() => window.location.reload()}
                >
                  <Activity className="w-4 h-4" />
                  <span>Refresh Stats</span>
                </Button>
                
                <Button 
                  variant="outline" 
                  className="flex items-center space-x-2 h-12"
                  onClick={() => navigate('/dashboard')}
                >
                  <Shield className="w-4 h-4" />
                  <span>User View</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;